import { type ReactNode } from 'react'

import { Field, FieldHelperText, Flex, SubText } from '@procurify/ui'

import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import {
  CUSTOM_FIELD_WITH_VALUE_OPTIONS_TYPES,
  isCustomFieldWithValueOptions,
} from '@webapp/core/utils/customfields'
import { BudgetCustomFieldSelect } from '../BudgetCustomFieldSelect'
import { BudgetCustomFieldValueSelect } from '../BudgetCustomFieldValueSelect'

export interface IBudgetCustomFieldConditionValue {
  field: Nullable<CustomField>
  /** Values as the backend stores them; a checkbox holds `[]` or one `CheckboxCustomFieldValues` value. */
  values: string[]
}

export interface IBudgetCustomFieldConditionProps {
  /** Prefix for the two inputs' ids, so each label targets its select. */
  id: string
  value: IBudgetCustomFieldConditionValue
  onChange: (value: IBudgetCustomFieldConditionValue) => void
  /** Called when the field select loses focus, so a form can mark the row as touched. */
  onFieldBlur?: () => void
  fieldLabel: ReactNode
  valuesLabel: ReactNode
  /** Hint shown under the field select and announced as its description. */
  fieldDescription?: ReactNode
  /** Hint shown under the values select and announced as its description. */
  valuesDescription?: ReactNode
  /** Shows this error below the field select and marks the field select as invalid. */
  fieldError?: string
  /** Shows this error below the values select and marks the values select as invalid. */
  valuesError?: string
  /** Fields chosen in other conditions, which this one can't pick again. */
  excludedFieldIds?: number[]
}

export const BudgetCustomFieldCondition = ({
  id,
  value,
  onChange,
  onFieldBlur,
  fieldLabel,
  valuesLabel,
  fieldDescription,
  valuesDescription,
  fieldError,
  valuesError,
  excludedFieldIds,
}: IBudgetCustomFieldConditionProps) => {
  const fieldInputId = `${id}-field`
  const valuesInputId = `${id}-values`
  const fieldDescriptionId = fieldDescription
    ? `${fieldInputId}-description`
    : undefined
  const valuesDescriptionId = valuesDescription
    ? `${valuesInputId}-description`
    : undefined
  const fieldErrorId = fieldError ? `${fieldInputId}-error` : undefined
  const fieldDescribedBy =
    [fieldDescriptionId, fieldErrorId].filter(Boolean).join(' ') || undefined
  const valuesErrorId = valuesError ? `${valuesInputId}-error` : undefined
  const valuesDescribedBy =
    [valuesDescriptionId, valuesErrorId].filter(Boolean).join(' ') || undefined

  const handleFieldChange = (field: Nullable<CustomField>) => {
    if (field?.id === value.field?.id) return
    onChange({ field, values: [] })
  }

  const handleValuesChange = (values: string[]) =>
    onChange({ ...value, values })

  const customFieldWithValueOptions =
    value.field && isCustomFieldWithValueOptions(value.field)
      ? value.field
      : null

  return (
    <Flex.Row>
      <Flex.Item width={1 / 2}>
        <Field
          label={fieldLabel}
          labelFor={fieldInputId}
          error={Boolean(fieldError)}
        >
          <BudgetCustomFieldSelect
            inputId={fieldInputId}
            value={value.field}
            onChange={handleFieldChange}
            onBlur={onFieldBlur}
            fieldTypes={CUSTOM_FIELD_WITH_VALUE_OPTIONS_TYPES}
            excludedFieldIds={excludedFieldIds}
            aria-describedby={fieldDescribedBy}
            aria-invalid={fieldError ? true : undefined}
            error={Boolean(fieldError)}
          />
          {fieldDescription && (
            <SubText id={fieldDescriptionId}>{fieldDescription}</SubText>
          )}
          {fieldError && (
            <FieldHelperText id={fieldErrorId} error>
              {fieldError}
            </FieldHelperText>
          )}
        </Field>
      </Flex.Item>
      <Flex.Item width={1 / 2}>
        <Field
          label={valuesLabel}
          labelFor={valuesInputId}
          error={Boolean(valuesError)}
        >
          <BudgetCustomFieldValueSelect
            inputId={valuesInputId}
            customField={customFieldWithValueOptions}
            value={value.values}
            onChange={handleValuesChange}
            aria-describedby={valuesDescribedBy}
            aria-invalid={valuesError ? true : undefined}
            error={Boolean(valuesError)}
          />
          {valuesDescription && (
            <SubText id={valuesDescriptionId}>{valuesDescription}</SubText>
          )}
          {valuesError && (
            <FieldHelperText id={valuesErrorId} error>
              {valuesError}
            </FieldHelperText>
          )}
        </Field>
      </Flex.Item>
    </Flex.Row>
  )
}
